<script setup>
import { ref, watch, computed } from 'vue';
import { Clapperboard, X } from 'lucide-vue-next';
import {usePage} from "@inertiajs/vue3";
import {trackFathomEvent} from "@/Helpers/analytics";

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    isOpen: Boolean,
    source: {
        type: String,
        default: 'casting_modal'
    },
    performer: {
        type: Object,
        default: null,
    },
    sfwTrigger: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['close']);

const isVisible = ref(false);
const email = ref('');

const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search;
    }
    return '';
});
const subText = computed(() => {
    if (props.sfwTrigger) {
        return 'Submit your pitches and help shape what gets made. Your ideas could become the next premium production.'
    }
    return 'Submit your pitch and help shape what gets made. Your ideas could become the next premium production.'
})
const signupText = computed(() => {
    if (props.sfwTrigger) {
        return 'Sign Up - $0.00*'
    }
    return 'Sign Up & Submit Pitch - $0.00*'
})

watch(() => props.isOpen, (open) => {
    isVisible.value = open;
    if (!open) email.value = '';
}, { immediate: true });

const joinOptionsData = usePage().props.joinOptions

const handleJoin = () => {
    const joinType = 'trial';

    if (joinOptionsData.button_trial) {
        let joinUrl = joinOptionsData.button_trial.url +
            (joinOptionsData.button_trial.url.includes('?') ? '&' : '?') +
            `custom8=${props.source}&vendor_option_id=2007234`;

        if (email.value.trim()) {
            joinUrl += `&email=${encodeURIComponent(email.value.trim())}`;
        }

        console.log(`++ JoinOptions: From ${props.source} Redirecting to`, joinUrl);

        localStorage.setItem('blue_join_options_source', props.source);
        localStorage.setItem('blue_join_options_join_type', joinType);
        localStorage.setItem('blue_join_options_url', joinUrl);

        trackFathomEvent('Biller Join Button Clicked')

        window.location.href = joinUrl;
    }
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="fixed inset-0 z-[51] flex items-center justify-center p-4 sm:p-6"
        >
            <!-- Backdrop -->
            <div
                class="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300"
                :class="isVisible ? 'opacity-100' : 'opacity-0'"
                aria-hidden="true"
            />

            <!-- Modal Container -->
            <div
                class="relative w-full max-w-[440px] max-h-[90vh] transform overflow-y-auto rounded-3xl bg-zinc-950 border border-zinc-800 shadow-2xl transition-all duration-300 ease-out"
                :class="isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                <!-- Close Button -->
                <button
                    class="absolute right-4 top-4 p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-zinc-900"
                    aria-label="Close modal"
                    @click="$emit('close')"
                >
                    <X class="h-5 w-5" />
                </button>

                <div class="p-4 sm:p-6 md:p-8">
                    <!-- Header Icon / Performer Thumbnail -->
                    <div class="flex justify-center mb-4 sm:mb-6">
                        <div class="relative">
                            <div class="absolute inset-0 bg-violet-500/20 blur-xl rounded-full" />
                            <template v-if="performer?.thumbnail_url">
                                <img
                                    :src="performer.thumbnail_url"
                                    :alt="performer.name"
                                    class="relative w-20 h-20 rounded-full object-cover object-top border-2 border-violet-500/50 shadow-lg"
                                />
                            </template>
                            <template v-else>
                                <div class="relative flex items-center justify-center w-20 h-20 bg-gradient-to-b from-zinc-800 to-zinc-950 rounded-full border border-zinc-700 shadow-inner">
                                    <Clapperboard class="w-10 h-10 text-violet-400" />
                                </div>
                            </template>
                            <div class="absolute -top-1 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 text-[10px] font-bold px-2 py-0.5 rounded-full border border-zinc-900">
                                FREE
                            </div>
                        </div>
                    </div>

                    <!-- Text Content -->
                    <div class="text-center mb-5 sm:mb-8">
                        <h2
                            id="modal-title"
                            class="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 tracking-tight"
                        >
                            <template v-if="performer">
                                Pitch a Scene with {{ performer.name }}
                            </template>
                            <template v-else>
                                Activate FREE Account
                                <span class="text-zinc-500 font-normal text-base sm:text-lg block sm:inline sm:text-2xl">
                                    (quick verify)
                                </span>
                            </template>
                        </h2>
                        <p class="text-zinc-400 text-xs sm:text-sm leading-relaxed">
                            <template v-if="performer">
                                Create a free account to submit your production pitch featuring <span class="text-white font-medium">{{ performer.name }}</span>. Your idea could become the next premium production.
                            </template>
                            <template v-else>
                                {{ subText }}
                            </template>
                        </p>
                    </div>

                    <!-- Casting Context: Benefits -->
                    <div class="bg-zinc-900/50 rounded-xl p-4 sm:p-5 mb-4 sm:mb-6 border border-zinc-800/50">
                        <div class="space-y-3">
                            <div class="flex items-start gap-3 text-sm">
                                <div class="w-6 h-6 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0 mt-0.5">
                                    <span class="text-violet-400 text-lg">🎬</span>
                                </div>
                                <div>
                                    <p class="text-white font-semibold">Submit Your Pitch</p>
                                    <p class="text-zinc-400 text-xs">
                                        <template v-if="performer">Describe your vision for a scene with {{ performer.name }} and send it to our production team</template>
                                        <template v-else>Share your creative ideas and help decide what content gets produced</template>
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-start gap-3 text-sm">
                                <div class="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                                    <img :src="STAR_ICON_URL" alt="star" class="w-3.5 h-3.5" />
                                </div>
                                <div>
                                    <p class="text-white font-semibold">Get 5 FREE Stars</p>
                                    <p class="text-zinc-400 text-xs">Use them to back your pitch or support other community ideas</p>
                                </div>
                            </div>
                            <div class="flex items-start gap-3 text-sm">
                                <div class="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                                    <span class="text-emerald-400 text-lg">✨</span>
                                </div>
                                <div>
                                    <p class="text-white font-semibold">Shape the Future</p>
                                    <p class="text-zinc-400 text-xs">
                                        <template v-if="performer">Help us decide if a scene with {{ performer.name }} gets made</template>
                                        <template v-else>Be part of the community that decides what gets made next</template>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Optional Email -->
                    <div class="mb-4">
                        <input
                            v-model="email"
                            type="email"
                            placeholder="Email (optional)"
                            class="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 focus:border-violet-500 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none transition-colors"
                        />
                    </div>

                    <!-- Actions -->
                    <div class="space-y-4">
                        <button
                            @click="handleJoin"
                            class="group relative overflow-hidden inline-flex items-center justify-center rounded-xl font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none active:scale-[0.98] bg-violet-600 text-white hover:bg-violet-500 shadow-lg shadow-violet-500/25 border border-violet-500 h-14 px-4 sm:px-6 text-base sm:text-lg w-full flex-col h-auto py-3 sm:py-4 gap-1"
                        >
                            <div class="absolute inset-0 bg-gradient-to-r from-violet-600 to-violet-500 opacity-100 transition-opacity group-hover:opacity-90" />
                            <div class="relative z-10 flex items-center justify-center gap-1 sm:gap-2 text-base sm:text-lg font-bold leading-tight">
                                <span class="text-center">
                                    <template v-if="performer">Sign Up & Pitch {{ performer.name }} - $0.00*</template>
                                    <template v-else>{{ signupText }}</template>
                                </span>
                            </div>
                        </button>

                        <div class="text-center">
                            <div class="relative z-10 text-[11px] font-medium text-zinc-500 opacity-90">
                                *Get 3 days free, cancel anytime.
                            </div>
                            <p class="text-center text-sm text-zinc-500 mt-2">
                                Already a member?
                                <a
                                    :href="route('members2.auth.login', { dest: currentUrl })"
                                    class="text-violet-400 hover:text-violet-300 font-semibold transition-colors"
                                >
                                    Sign in
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
</style>
